import {
  CadenceKey,
  CadenceSchedule,
  DEFAULT_CADENCE_SCHEDULE,
} from "./guidanceCadence";
import {
  GUIDANCE_ROLES,
  GUIDANCE_ROLE_KEYS,
  GuidanceRole,
} from "./guidanceRole";
import { normalizeRoleName } from "../Constants/RoleConstants";

/** Platform guidance_hub_settings keys (public schema). */
export const GUIDANCE_HUB_ENABLED_KEY = "guidance_hub_enabled";
export const GUIDANCE_CADENCE_SCHEDULE_KEY = "guidance_cadence_schedule";

export type GuidanceHubRoleToggles = Record<string, boolean>;

export interface GuidanceHubEnabledValue {
  enabled: boolean;
  roles: GuidanceHubRoleToggles;
}

/** Per-role visibility — all 11 authored roles default on (each has content). */
export const DEFAULT_GUIDANCE_HUB_ROLES: GuidanceHubRoleToggles =
  Object.fromEntries(GUIDANCE_ROLE_KEYS.map(role => [role, true]));

const GUIDANCE_HUB_ROLE_LABELS: Record<GuidanceRole, string> = {
  ae: "Account Executive",
  csm: "Customer Success Manager",
  se: "Solution Architect",
  tam: "Technical Account Manager",
  bcs: "Business Continuity Services",
  sdr: "Sales Development Representative",
  vp: "Vice President",
  executive_sponsor: "Executive Sponsor",
  executive: "Executive",
  executive_sponsor_lead: "Executive Sponsor Lead",
  c_level: "C-Level / CXO",
};

/** Display order + labels for the Org Settings per-role toggle list. */
export const GUIDANCE_HUB_ROLE_OPTIONS: {
  role: GuidanceRole;
  label: string;
}[] = GUIDANCE_ROLE_KEYS.map(role => {
  const short = GUIDANCE_ROLES.find(r => r.key === role)?.shortLabel ?? role;
  return {
    role,
    label: `${GUIDANCE_HUB_ROLE_LABELS[role]} (${short})`,
  };
});

export const DEFAULT_GUIDANCE_HUB_ENABLED: GuidanceHubEnabledValue = {
  enabled: true,
  roles: { ...DEFAULT_GUIDANCE_HUB_ROLES },
};

export const normalizeGuidanceHubEnabled = (
  raw: unknown
): GuidanceHubEnabledValue => {
  if (!raw || typeof raw !== "object") return DEFAULT_GUIDANCE_HUB_ENABLED;
  const value = raw as Partial<GuidanceHubEnabledValue>;
  const rawRoles =
    value.roles && typeof value.roles === "object" ? value.roles : {};
  const roles: GuidanceHubRoleToggles = { ...DEFAULT_GUIDANCE_HUB_ROLES };
  for (const [role, on] of Object.entries(rawRoles)) {
    roles[role] = Boolean(on);
  }
  return { enabled: Boolean(value.enabled ?? true), roles };
};

export const isGuidanceHubEnabledForRole = (
  userRole: string | { name?: string } | null | undefined,
  roles: GuidanceHubRoleToggles
): boolean => {
  const normalized = normalizeRoleName(userRole);
  if (!normalized) return false;
  return roles[normalized] === true;
};

/** Org-wide kill switch plus per-role toggle for the signed-in user. */
export const isGuidanceHubActiveForUser = (
  hubEnabled: boolean,
  roles: GuidanceHubRoleToggles,
  userRole: string | { name?: string } | null | undefined
): boolean => hubEnabled && isGuidanceHubEnabledForRole(userRole, roles);

/** Role tabs the Hub should offer for enabled roles (vendor_admin uses all keys). */
export const getVisibleGuidanceRoles = (
  roles: GuidanceHubRoleToggles
): GuidanceRole[] => GUIDANCE_ROLE_KEYS.filter(role => roles[role] === true);

const isValidDayArray = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.every(v => Number.isInteger(v) && v >= 0 && v <= 6);

export const normalizeCadenceSchedule = (raw: unknown): CadenceSchedule => {
  if (!raw || typeof raw !== "object") return DEFAULT_CADENCE_SCHEDULE;
  const value = raw as Partial<Record<keyof CadenceSchedule, unknown>>;
  const keys: (keyof CadenceSchedule)[] = [
    "daily",
    "weekly",
    "monthly",
    "quarterly",
  ];
  const result = {} as CadenceSchedule;
  for (const key of keys) {
    result[key] = isValidDayArray(value[key])
      ? (value[key] as number[])
      : DEFAULT_CADENCE_SCHEDULE[key];
  }
  return result;
};

export const CADENCE_SCHEDULE_ASSIGNABLE: Exclude<CadenceKey, "biweekly">[] = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
];
